import { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { toast } from "sonner";
import {
  Loader2,
  Upload,
  FileText,
  X,
  CheckCircle2,
  FilePlus,
  ListChecks,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PdfBuilderEmbed } from "@/features/org-forms/pdf/pdf-builder-embed";
import type { BuilderEmbed } from "@/features/org-forms/pdf/pdf-builder-embed";

interface SigningDocumentSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationBusinessUnitId: string;
  jobPostId: string;
  candidateUserId: string;
  hiringProcessItemId: string;
  /** Called after the slot is set up (refresh the candidate's items). */
  onDone?: () => void;
}

type Step =
  | { kind: "choose" }
  | { kind: "upload" }
  | { kind: "builder"; formId: string; embed: BuilderEmbed }
  | { kind: "pick" }
  | { kind: "publishing" }
  | { kind: "done" };

/**
 * Org sets up a `document_signing` slot for one candidate: either UPLOAD a PDF
 * (map fields → publish) or PICK a reusable two-party form. Both point the
 * candidate's progress row at the chosen form; the org then signs its fields and
 * the candidate signs the rest.
 */
export function SigningDocumentSetupDialog({
  open,
  onOpenChange,
  organizationBusinessUnitId,
  jobPostId,
  candidateUserId,
  hiringProcessItemId,
  onDone,
}: SigningDocumentSetupDialogProps) {
  const trpc = useTRPC();
  const [step, setStep] = useState<Step>({ kind: "choose" });
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastEmbedRef = useRef<BuilderEmbed | null>(null);

  const common = {
    organizationBusinessUnitId,
    jobPostId,
    candidateUserId,
    hiringProcessItemId,
  };

  const uploadMutation = useMutation(
    trpc.organizationForm.setUpSigningDocumentUpload.mutationOptions(),
  );
  const pickMutation = useMutation(
    trpc.organizationForm.setUpSigningDocumentFromTemplate.mutationOptions(),
  );
  const syncMutation = useMutation(
    trpc.organizationForm.syncPdfFields.mutationOptions(),
  );
  const publishMutation = useMutation(
    trpc.organizationForm.publishPdfForm.mutationOptions(),
  );
  const { data: templates } = useQuery(
    trpc.organizationForm.listSigningTemplates.queryOptions(
      { organizationBusinessUnitId },
      { enabled: open && step.kind === "pick" },
    ),
  );

  const isBusy =
    uploadMutation.isPending ||
    pickMutation.isPending ||
    syncMutation.isPending ||
    publishMutation.isPending;

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStep({ kind: "choose" });
      setName("");
      setFile(null);
    }, 300);
  };

  const handleUpload = async () => {
    if (!file || !name.trim()) {
      return;
    }
    const pdfBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    try {
      const result = await uploadMutation.mutateAsync({
        ...common,
        name: name.trim(),
        fileName: file.name,
        pdfBase64,
      });
      if (result.builderEmbed) {
        lastEmbedRef.current = result.builderEmbed;
        setStep({
          kind: "builder",
          formId: result.form.id,
          embed: result.builderEmbed,
        });
      } else {
        await handlePublish(result.form.id);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to upload");
    }
  };

  const handlePublish = async (formId: string) => {
    setStep({ kind: "publishing" });
    try {
      const synced = await syncMutation.mutateAsync({
        organizationBusinessUnitId,
        formId,
      });
      if ((synced?.fields ?? []).length === 0) {
        toast.error("Add at least one field in the editor, then try again.");
        if (lastEmbedRef.current) {
          setStep({ kind: "builder", formId, embed: lastEmbedRef.current });
        }
        return;
      }
      await publishMutation.mutateAsync({ organizationBusinessUnitId, formId });
      toast.success("Document ready");
      onDone?.();
      setStep({ kind: "done" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to publish");
      if (lastEmbedRef.current) {
        setStep({ kind: "builder", formId, embed: lastEmbedRef.current });
      }
    }
  };

  const handlePick = async (formId: string) => {
    try {
      await pickMutation.mutateAsync({ ...common, formId });
      toast.success("Document ready");
      onDone?.();
      setStep({ kind: "done" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to set up");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className={
          step.kind === "builder"
            ? "h-[90vh] flex flex-col overflow-hidden"
            : "w-[calc(100vw-2rem)] max-w-xl"
        }
        style={
          step.kind === "builder"
            ? { width: "90vw", maxWidth: "none" }
            : undefined
        }
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {step.kind === "done" ? "PDF ready" : "Set up PDF to complete"}
          </DialogTitle>
          {step.kind === "choose" && (
            <DialogDescription>
              Upload a PDF for this candidate, or pick a reusable two-party
              form.
            </DialogDescription>
          )}
        </DialogHeader>

        {step.kind === "choose" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <button
              type="button"
              onClick={() => setStep({ kind: "upload" })}
              className="flex flex-col items-center gap-2 rounded-lg border border-gray-200 p-5 hover:border-blue-300 hover:bg-blue-50/50"
            >
              <FilePlus size={22} className="text-blue-600" />
              <span className="text-sm font-medium">Upload a PDF</span>
              <span className="text-xs text-gray-500 text-center">
                A candidate-specific document you map + route now.
              </span>
            </button>
            <button
              type="button"
              onClick={() => setStep({ kind: "pick" })}
              className="flex flex-col items-center gap-2 rounded-lg border border-gray-200 p-5 hover:border-blue-300 hover:bg-blue-50/50"
            >
              <ListChecks size={22} className="text-blue-600" />
              <span className="text-sm font-medium">Pick a reusable form</span>
              <span className="text-xs text-gray-500 text-center">
                A pre-made two-party form (org + candidate fields).
              </span>
            </button>
          </div>
        )}

        {step.kind === "upload" && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Document name</Label>
              <Input
                placeholder="e.g. Offer Letter"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>PDF</Label>
              {file ? (
                <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                  <FileText size={18} className="text-blue-600 shrink-0" />
                  <span className="text-sm flex-1 truncate">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    aria-label="Remove"
                  >
                    <X size={16} className="text-gray-400" />
                  </button>
                </div>
              ) : (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => inputRef.current?.click()}
                  onKeyDown={(e) =>
                    e.key === "Enter" && inputRef.current?.click()
                  }
                  className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 px-4 py-8 cursor-pointer hover:border-blue-300"
                >
                  <Upload size={24} className="text-gray-400" />
                  <p className="text-sm text-gray-600">Browse for a PDF</p>
                  <input
                    ref={inputRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setFile(f);
                      }
                      e.target.value = "";
                    }}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep({ kind: "choose" })}
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={() => void handleUpload()}
                disabled={isBusy || !file || !name.trim()}
              >
                {uploadMutation.isPending ? (
                  <>
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  "Continue to Field Mapping"
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step.kind === "pick" && (
          <div className="space-y-3">
            <div className="max-h-72 overflow-auto rounded-md border border-gray-100 divide-y divide-gray-50">
              {(templates ?? []).length === 0 ? (
                <p className="text-sm text-gray-400 italic px-3 py-4">
                  No reusable two-party forms yet. Create one in Forms (with at
                  least one Organization field), or upload a PDF instead.
                </p>
              ) : (
                (templates ?? []).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    disabled={isBusy}
                    onClick={() => void handlePick(t.id)}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50/50 flex items-center gap-2"
                  >
                    <FileText size={14} className="text-blue-600 shrink-0" />
                    {t.name}
                  </button>
                ))
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep({ kind: "choose" })}
              >
                Back
              </Button>
            </DialogFooter>
          </div>
        )}

        {step.kind === "builder" && (
          <div className="flex flex-col flex-1 min-h-0 gap-4">
            <p className="text-sm text-gray-600 shrink-0">
              Map the fields and assign each to the candidate or organization.
              When done, click <strong>Done — Import Fields</strong>.
            </p>
            <div className="flex-1 min-h-0 overflow-auto rounded-md border border-gray-200">
              <PdfBuilderEmbed embed={step.embed} />
            </div>
            <DialogFooter className="shrink-0">
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void handlePublish(step.formId)}
                disabled={isBusy}
              >
                {isBusy ? (
                  <>
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  "Done — Import Fields"
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step.kind === "publishing" && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
            <p className="text-sm text-gray-500">Importing fields...</p>
          </div>
        )}

        {step.kind === "done" && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <CheckCircle2 className="h-10 w-10 text-green-500" />
            <p className="text-base font-medium text-gray-800 text-center">
              Document is set up. Sign your fields, then it routes to the
              candidate.
            </p>
            <Button type="button" onClick={close}>
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
